import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneOutgoing, PhoneIncoming, PhoneMissed, PhoneOff, Clock, Ban, Voicemail, X, Loader2 } from "lucide-react";
import api from "@/lib/crmApi";
import { toast } from "sonner";
import { useLiveData } from "@/lib/useLiveData";
import LiveBadge from "@/crm/components/LiveBadge";
import { cn } from "@/lib/utils";
import { AVATAR_BG } from "./shared";

const fmtH = (s) => {
  const h = Math.floor((s || 0) / 3600);
  const m = Math.floor(((s || 0) % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m} min`;
};

// « Aujourd'hui » n'est pas « les dernières 24 h » : la journée commence à
// minuit chez le commercial, et lui seul connaît son fuseau. Le navigateur
// envoie donc la borne exacte (`since`), là où les autres périodes se disent
// en fenêtre glissante (`days`).
const PERIODS = ["today", 7, 30, 90];
const PERIOD_LABEL = {
  today: "Aujourd'hui", 7: "7 derniers jours",
  30: "30 derniers jours", 90: "90 derniers jours",
};
const PERIOD_SHORT = { today: "Auj.", 7: "7 j", 30: "30 j", 90: "90 j" };

/** Borne de la période, dite comme on la lirait à voix haute. */
const fmtSince = (iso, period) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return period === "today"
    ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    : `le ${d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} à ${
        d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const periodQuery = (period) => (period === "today"
  ? `since=${encodeURIComponent(startOfToday())}`
  : `days=${period}`);

// Les appels refusés (pays interdit, plafond, numéro bloqué) et la messagerie
// ont leur propre compteur : les noyer dans les volumes masquait précisément ce
// qu'un responsable doit voir.
const CARDS = [
  { key: "outbound", label: "Appels sortants", icon: PhoneOutgoing, cls: "text-indigo-600 bg-indigo-50" },
  { key: "inbound_prospect", label: "Entrants prospects", icon: PhoneIncoming, cls: "text-sky-600 bg-sky-50" },
  { key: "inbound_client", label: "Entrants clients", icon: PhoneIncoming, cls: "text-emerald-600 bg-emerald-50" },
  { key: "missed", label: "Appels manqués", icon: PhoneMissed, cls: "text-orange-600 bg-orange-50" },
  { key: "abandoned", label: "Raccrochés avant réponse", icon: PhoneOff, cls: "text-yellow-600 bg-yellow-50" },
  { key: "rejected", label: "Appels refusés", icon: Ban, cls: "text-rose-600 bg-rose-50" },
  { key: "voicemail", label: "Messagerie", icon: Voicemail, cls: "text-amber-600 bg-amber-50" },
];

const RESULT_CLS = {
  REPONDU: "text-emerald-600", TERMINE: "text-emerald-600", MANQUE: "text-orange-600",
  REJETE: "text-rose-600", RENVOI_SPECIAL: "text-rose-600", MESSAGERIE: "text-amber-600",
  ABANDONNE: "text-yellow-600", EN_COURS: "text-sky-600",
};

const CELL = "px-3 py-2.5 text-right font-mono";

export default function TelephonyStats() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(30);
  const [detail, setDetail] = useState(null); // { key, label, items, loading }
  const { data, refreshing, lastUpdated, refresh } = useLiveData(
    `/twilio/stats?${periodQuery(period)}`, { interval: 15000 });

  // Le détail d'un compteur est demandé au journal avec les filtres fournis par
  // le backend AVEC le compteur : chiffre et liste ne peuvent donc pas diverger.
  const ouvrirDetail = (card) => {
    if (!data.drilldown?.[card.key]) return;
    setDetail((d) => (d?.key === card.key
      ? null                                        // deuxième clic : on referme
      : { key: card.key, label: card.label, items: [], loading: true }));
  };

  // La liste ouverte se recharge dès que ses filtres changent — c'est-à-dire au
  // changement de période. Sans ça elle gardait les appels de la période
  // précédente sous un intitulé qui, lui, avait changé : le compteur disait
  // « aujourd'hui », la liste montrait le mois.
  const openKey = detail?.key || "";
  const query = useMemo(() => {
    const filtres = openKey ? data?.drilldown?.[openKey] : null;
    return filtres ? new URLSearchParams({ ...filtres, limit: 200 }).toString() : "";
  }, [openKey, data]);

  useEffect(() => {
    if (!openKey || !query) return undefined;
    let cancelled = false;
    api.get(`/telephony/calls?${query}`)
      .then(({ data: items }) => {
        if (cancelled) return;
        setDetail((d) => (d && d.key === openKey
          ? { ...d, items: items || [], loading: false } : d));
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Détail indisponible");
        setDetail(null);
      });
    return () => { cancelled = true; };
  }, [openKey, query]);

  if (!data) return <div className="text-slate-400 text-sm">Chargement des statistiques…</div>;
  const t = data.totals;
  const totalCalls = t.outbound + t.inbound_prospect + t.inbound_client
    + t.rejected + t.in_progress;

  return (
    <div data-testid="telephony-stats">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-lg text-slate-900">Communications</h3>
          {/* Borne réellement appliquée, telle que le serveur l'a calculée : deux
              périodes peuvent afficher les mêmes chiffres simplement parce que
              tous les appels sont récents. Le dire évite de prendre un filtre
              qui marche pour un filtre en panne. */}
          <p data-testid="stats-window" className="mt-0.5 text-[12.5px] text-slate-400">
            {PERIOD_LABEL[period]}{data.since ? ` · depuis ${fmtSince(data.since, period)}` : ""}
            {` · ${totalCalls} appel${totalCalls > 1 ? "s" : ""}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LiveBadge refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
          {/* Segmenté aujourd'hui/7/30/90 — remplace l'ancien <select data-testid="stats-period"> */}
          <div data-testid="stats-period" className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                data-testid={`stats-period-${p}`}
                onClick={() => setPeriod(p)}
                title={PERIOD_LABEL[p]}
                aria-pressed={period === p}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap",
                  period === p ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"
                )}
              >
                {p === "today" ? (
                  <>
                    <span className="sm:hidden">{PERIOD_SHORT[p]}</span>
                    <span className="hidden sm:inline">{PERIOD_LABEL[p]}</span>
                  </>
                ) : PERIOD_SHORT[p]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Compteurs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
        {CARDS.map((c) => (
          <StatCard key={c.key} testid={`stat-${c.key}`} label={c.label} value={t[c.key]}
            hint={c.key === "voicemail" && t.voicemail_messages
              ? `dont ${t.voicemail_messages} message${t.voicemail_messages > 1 ? "s" : ""} laissé${t.voicemail_messages > 1 ? "s" : ""}`
              : t[c.key] ? "Voir les numéros" : PERIOD_LABEL[period]} icon={c.icon} cls={c.cls}
            onClick={() => ouvrirDetail(c)} active={detail?.key === c.key} />
        ))}
        <StatCard
          testid="stat-duration"
          label="Durée totale"
          value={fmtH(t.total_duration)}
          hint={t.avg_duration ? `moy. ${fmtH(t.avg_duration)} / appel décroché` : PERIOD_LABEL[period]}
          icon={Clock}
          cls="text-violet-600 bg-violet-50"
        />
      </div>

      {detail && (
        <CallDetail detail={detail} onClose={() => setDetail(null)} navigate={navigate} period={period} />
      )}

      {/* Par commercial — tableau en lg+, cartes en dessous */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hidden lg:block">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-medium text-slate-700">Par commercial</div>
        <table className="w-full text-sm table-fixed">
          <thead>
            <tr className="text-xs font-mono uppercase tracking-wider text-slate-400 bg-slate-50/70 border-b border-slate-100">
              <th className="px-5 py-2.5 text-left">Commercial</th>
              <th className="px-3 py-2.5 text-right w-[104px]">Sortants</th>
              <th className="px-3 py-2.5 text-right w-[116px]">Entr. prosp.</th>
              <th className="px-3 py-2.5 text-right w-[116px]">Entr. clients</th>
              <th className="px-3 py-2.5 text-right w-[104px]">Manqués</th>
              <th className="px-5 py-2.5 text-right w-[112px]">Durée</th>
            </tr>
          </thead>
          <tbody>
            {data.per_user.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">Aucune donnée sur la période</td></tr>
            ) : data.per_user.map((u, i) => (
              <tr key={u.user_id} data-testid={`stat-row-${u.user_id}`} className="border-b border-slate-50 hover:bg-slate-50/70">
                <td className="px-5 py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={u.name} i={i} />
                    <span className="font-medium text-slate-800 truncate">{u.name || "—"}</span>
                  </div>
                </td>
                <td className={cn(CELL, "text-indigo-600")}>{u.outbound}</td>
                <td className={cn(CELL, "text-sky-600")}>{u.inbound_prospect}</td>
                <td className={cn(CELL, "text-emerald-600")}>{u.inbound_client}</td>
                <td className={cn(CELL, "text-orange-600")}>{u.missed}</td>
                <td className="px-5 py-2.5 text-right font-mono text-slate-400">{fmtH(u.duration)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-2.5 lg:hidden">
        {data.per_user.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 py-8 text-center text-sm text-slate-400">Aucune donnée sur la période</div>
        )}
        {data.per_user.map((u, i) => (
          <div key={u.user_id} data-testid={`stat-row-${u.user_id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={u.name} i={i} />
              <span className="font-medium text-slate-800 truncate">{u.name || "—"}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Metric label="Sortants" value={u.outbound} cls="text-indigo-600" />
              <Metric label="Entrants prospects" value={u.inbound_prospect} cls="text-sky-600" />
              <Metric label="Entrants clients" value={u.inbound_client} cls="text-emerald-600" />
              <Metric label="Manqués" value={u.missed} cls="text-orange-600" />
              <Metric label="Durée" value={fmtH(u.duration)} cls="text-slate-500" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CallDetail({ detail, onClose, navigate, period }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 mb-5 overflow-hidden" data-testid="stat-detail">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-100">
        <div className="text-sm font-medium text-slate-700">
          {detail.label} · {detail.loading ? "…" : `${detail.items.length} appel${detail.items.length > 1 ? "s" : ""}`}
          <span className="text-slate-400 font-normal"> — {PERIOD_LABEL[period]}</span>
        </div>
        <button onClick={onClose} data-testid="stat-detail-close"
          className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X size={15} /></button>
      </div>
      {detail.loading ? (
        <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-400">
          <Loader2 size={15} className="animate-spin" /> Chargement…
        </div>
      ) : detail.items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-400">Aucun appel sur la période.</p>
      ) : (
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
          {detail.items.map((c) => (
            <div key={c.id} data-testid="stat-detail-row"
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-2.5 text-sm">
              <span className="font-mono text-slate-800">{c.phone_number}</span>
              {(c.contact_name || c.company_name) && (
                <span className="text-xs text-slate-600 truncate">{c.contact_name || c.company_name}</span>
              )}
              <span className={cn("text-xs font-medium", RESULT_CLS[c.result] || "text-slate-500")}>
                {c.result}{c.final_destination ? ` · ${c.final_destination}` : ""}
              </span>
              <span className="text-xs text-slate-400">
                {c.direction === "SORTANT" ? "sortant" : "entrant"}
                {c.answered_by_name ? ` · ${c.answered_by_name}` : ""}
              </span>
              <span className="ml-auto text-xs text-slate-400 shrink-0">
                {c.created_at ? new Date(c.created_at).toLocaleString("fr-FR") : ""}
              </span>
              {c.company_id && (
                <button onClick={() => navigate(`/entreprises/${c.company_id}`)}
                  className="text-xs text-indigo-600 hover:underline shrink-0">
                  {c.company_name || "Ouvrir la fiche"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ testid, label, value, hint, icon: Icon, cls, onClick, active }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag data-testid={testid} onClick={onClick}
      className={cn("bg-white rounded-2xl border p-4 text-left w-full",
        active ? "border-slate-900" : "border-slate-200",
        onClick && "hover:border-slate-300 transition-colors")}>
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-xs text-slate-500">{label}</span>
        <span className={cn("h-7 w-7 rounded-lg flex items-center justify-center shrink-0", cls)}>
          <Icon size={15} />
        </span>
      </div>
      <div className="mt-2.5 text-2xl font-mono text-slate-900">{value}</div>
      <div className="mt-1 text-[11.5px] text-slate-400">{hint}</div>
    </Tag>
  );
}

function Avatar({ name, i }) {
  return (
    <div className={cn("h-8 w-8 shrink-0 rounded-full text-white flex items-center justify-center text-xs font-semibold", AVATAR_BG[i % AVATAR_BG.length])}>
      {(name || "?")[0]}
    </div>
  );
}

function Metric({ label, value, cls }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-slate-50 pb-1.5">
      <span className="text-xs text-slate-400">{label}</span>
      <span className={cn("font-mono text-sm", cls)}>{value}</span>
    </div>
  );
}
